import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { WorkspaceId } from "./workspaces/types.js";

interface UiState {
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
  /**
   * WS-NAV rule 3 — the workspace the user was last in. Persisted so a
   * returning user lands where they left off instead of on Home. `null` for a
   * first-time user (who therefore gets the Home card overview).
   */
  activeWorkspaceId: WorkspaceId | null;
  setActiveWorkspaceId: (id: WorkspaceId | null) => void;
  /** Mobile drawer open state — ephemeral, never persisted. */
  mobileNavOpen: boolean;
  setMobileNavOpen: (open: boolean) => void;
}

/**
 * App-shell UI state that should persist across reloads but stays out of the
 * URL: the sidebar collapse flag and the last-used workspace. Future ergonomic
 * switches (e.g. compact density preference) belong here too.
 */
export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      sidebarCollapsed: false,
      toggleSidebar: () => {
        set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed }));
      },
      activeWorkspaceId: null,
      setActiveWorkspaceId: (id) => {
        set({ activeWorkspaceId: id });
      },
      mobileNavOpen: false,
      setMobileNavOpen: (open) => {
        set({ mobileNavOpen: open });
      },
    }),
    {
      name: "aero-lyse:ui",
      storage: createJSONStorage(() => localStorage),
      // Only persist the durable preferences, not the actions and not the
      // ephemeral drawer flag.
      partialize: (s) => ({
        sidebarCollapsed: s.sidebarCollapsed,
        activeWorkspaceId: s.activeWorkspaceId,
      }),
    },
  ),
);
